import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Archive,
  ArrowRightLeft,
  Boxes,
  FileText,
  History,
  MapPin,
  PackagePlus,
  Plus,
  RefreshCw,
  ShieldAlert,
  Upload,
  Wrench,
} from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Textarea } from "../../components/ui/textarea";
import { Loading, PageHeader, StatCard } from "../../components/shared";

const assetDefaults = {
  name: "",
  category_id: "",
  location: "",
  serial_number: "",
  status: "ACTIVE",
  condition: "GOOD",
  criticality: "MEDIUM",
  warranty_start: "",
  warranty_end: "",
  purchase_date: "",
  purchase_price: "",
  supplier_reference: "",
  invoice_number: "",
  manufacturer: "",
  model_number: "",
  notes: "",
};

const inventoryDefaults = {
  name: "",
  category_id: "",
  uom: "PCS",
  minimum_stock: 0,
  reorder_level: 0,
};

function Field({ children, label }) {
  return (
    <div>
      <Label className="mb-1.5 block text-xs text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}

function EmptyMessage({ children, testid }) {
  return (
    <div
      className={
        "border border-dashed bg-muted/30 px-5 py-10 text-center text-sm " +
        "text-muted-foreground"
      }
      data-testid={testid}
    >
      {children}
    </div>
  );
}

export default function AssetsInventory() {
  return (
    <div data-testid="assets-inventory-page">
      <PageHeader
        title="Assets & Inventory"
        subtitle="Keep equipment, warranty, stores, stock, and operational history in one register."
      />
      <Tabs defaultValue="assets">
        <TabsList
          className="mb-6 h-auto flex-wrap"
          data-testid="assets-inventory-tabs"
        >
          <TabsTrigger value="assets" data-testid="assets-tab">
            <Wrench className="mr-1.5 h-4 w-4" />
            Asset Register
          </TabsTrigger>
          <TabsTrigger value="inventory" data-testid="inventory-tab">
            <Boxes className="mr-1.5 h-4 w-4" />
            Inventory
          </TabsTrigger>
        </TabsList>
        <TabsContent value="assets">
          <AssetRegister />
        </TabsContent>
        <TabsContent value="inventory">
          <InventoryWorkspace />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AssetRegister() {
  const [assets, setAssets] = useState(null);
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(assetDefaults);
  const [selectedId, setSelectedId] = useState("");
  const [categoryName, setCategoryName] = useState("");

  const load = async () => {
    try {
      const [assetResponse, categoryResponse] = await Promise.all([
        api.get("/society-assets/assets"),
        api.get("/society-assets/asset-categories"),
      ]);
      setAssets(assetResponse.data);
      setCategories(categoryResponse.data.filter((item) => item.active));
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not load the asset register",
      );
      setAssets([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const createCategory = async () => {
    try {
      await api.post("/society-assets/asset-categories", {
        name: categoryName,
      });
      setCategoryName("");
      toast.success("Asset category added");
      load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not add asset category",
      );
    }
  };

  const saveAsset = async () => {
    try {
      if (!form.name || !form.category_id) {
        toast.error("Asset name and category are required");
        return;
      }
      await api.post("/society-assets/assets", form);
      toast.success("Asset added to the register");
      setForm(assetDefaults);
      setDialogOpen(false);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not add asset");
    }
  };

  if (!assets) {
    return <Loading />;
  }

  return (
    <div>
      <section className="mb-6 grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
        <div className="border bg-card p-4">
          <div className="mb-2 font-display text-sm font-bold">
            Asset Categories
          </div>
          <div
            className="flex flex-wrap gap-2"
            data-testid="asset-category-list"
          >
            {categories.map((category) => (
              <span
                key={category.category_id}
                className="border bg-muted/40 px-2 py-1 text-xs"
                data-testid={`asset-category-${category.category_id}`}
              >
                {category.name}
              </span>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Input
              className="max-w-xs"
              data-testid="asset-category-name-input"
              onChange={(event) => setCategoryName(event.target.value)}
              placeholder="Add category"
              value={categoryName}
            />
            <Button
              data-testid="asset-category-add-button"
              onClick={createCategory}
              size="sm"
              type="button"
              variant="outline"
            >
              <Plus className="mr-1 h-4 w-4" />
              Add
            </Button>
          </div>
        </div>
        <Button
          className="gap-2"
          data-testid="asset-create-button"
          onClick={() => setDialogOpen(true)}
        >
          <Plus className="h-4 w-4" />
          Add Asset
        </Button>
      </section>

      {assets.length === 0 ? (
        <EmptyMessage testid="asset-empty-state">
          Add installed equipment such as lifts, pumps, CCTV, generators, and
          fire systems.
        </EmptyMessage>
      ) : (
        <div
          className="overflow-x-auto border bg-card"
          data-testid="asset-register-table"
        >
          <table className="w-full min-w-[980px] text-sm">
            <thead className="bg-muted/60 text-left text-xs text-muted-foreground">
              <tr>
                <th className="p-3">Asset</th>
                <th className="p-3">Location</th>
                <th className="p-3">Condition</th>
                <th className="p-3">Warranty</th>
                <th className="p-3">Status</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {assets.map((asset) => (
                <tr
                  className="border-t"
                  data-testid={`asset-row-${asset.asset_id}`}
                  key={asset.asset_id}
                >
                  <td className="p-3">
                    <div className="font-medium">{asset.name}</div>
                    <div className="font-mono text-xs text-muted-foreground">
                      {asset.asset_code} · {asset.category_name}
                    </div>
                  </td>
                  <td className="p-3">{asset.location || "Not recorded"}</td>
                  <td className="p-3">{asset.condition}</td>
                  <td className="p-3">
                    {asset.warranty_end || "Not recorded"}
                  </td>
                  <td className="p-3">
                    <span className="bg-primary/10 px-2 py-1 text-xs text-primary">
                      {asset.status}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <Button
                      data-testid={`asset-open-${asset.asset_id}`}
                      onClick={() => setSelectedId(asset.asset_id)}
                      size="sm"
                      variant="outline"
                    >
                      Asset 360
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog onOpenChange={setDialogOpen} open={dialogOpen}>
        <DialogContent
          className="max-h-[90vh] max-w-3xl overflow-y-auto"
          data-testid="asset-create-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display">Add asset</DialogTitle>
          </DialogHeader>
          <AssetForm categories={categories} form={form} onChange={setForm} />
          <Button data-testid="asset-save-button" onClick={saveAsset}>
            Save Asset
          </Button>
        </DialogContent>
      </Dialog>
      <AssetDetail
        assetId={selectedId}
        onChanged={load}
        onClose={() => setSelectedId("")}
      />
    </div>
  );
}

function AssetForm({ categories, form, onChange }) {
  const update = (key, value) => onChange({ ...form, [key]: value });
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label="Asset name *">
        <Input
          data-testid="asset-name-input"
          onChange={(event) => update("name", event.target.value)}
          value={form.name}
        />
      </Field>
      <Field label="Category *">
        <Select
          onValueChange={(value) => update("category_id", value)}
          value={form.category_id}
        >
          <SelectTrigger data-testid="asset-category-select">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((category) => (
              <SelectItem
                key={category.category_id}
                value={category.category_id}
              >
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Location">
        <Input
          data-testid="asset-location-input"
          onChange={(event) => update("location", event.target.value)}
          value={form.location}
        />
      </Field>
      <Field label="Serial number">
        <Input
          data-testid="asset-serial-input"
          onChange={(event) => update("serial_number", event.target.value)}
          value={form.serial_number}
        />
      </Field>
      <Field label="Status">
        <Select
          onValueChange={(value) => update("status", value)}
          value={form.status}
        >
          <SelectTrigger data-testid="asset-status-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[
              "ACTIVE",
              "IN_SERVICE",
              "UNDER_REPAIR",
              "OUT_OF_SERVICE",
              "STANDBY",
            ].map((item) => (
              <SelectItem key={item} value={item}>
                {item}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Condition">
        <Select
          onValueChange={(value) => update("condition", value)}
          value={form.condition}
        >
          <SelectTrigger data-testid="asset-condition-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {["GOOD", "FAIR", "POOR", "CRITICAL"].map((item) => (
              <SelectItem key={item} value={item}>
                {item}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Criticality">
        <Select
          onValueChange={(value) => update("criticality", value)}
          value={form.criticality}
        >
          <SelectTrigger data-testid="asset-criticality-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((item) => (
              <SelectItem key={item} value={item}>
                {item}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Manufacturer">
        <Input
          data-testid="asset-manufacturer-input"
          onChange={(event) => update("manufacturer", event.target.value)}
          value={form.manufacturer}
        />
      </Field>
      <Field label="Model number">
        <Input
          data-testid="asset-model-input"
          onChange={(event) => update("model_number", event.target.value)}
          value={form.model_number}
        />
      </Field>
      <Field label="Purchase date">
        <Input
          data-testid="asset-purchase-date-input"
          onChange={(event) => update("purchase_date", event.target.value)}
          type="date"
          value={form.purchase_date}
        />
      </Field>
      <Field label="Purchase price">
        <Input
          data-testid="asset-purchase-price-input"
          min="0"
          onChange={(event) => update("purchase_price", event.target.value)}
          type="number"
          value={form.purchase_price}
        />
      </Field>
      <Field label="Supplier">
        <Input
          data-testid="asset-supplier-input"
          onChange={(event) => update("supplier_reference", event.target.value)}
          value={form.supplier_reference}
        />
      </Field>
      <Field label="Invoice number">
        <Input
          data-testid="asset-invoice-input"
          onChange={(event) => update("invoice_number", event.target.value)}
          value={form.invoice_number}
        />
      </Field>
      <Field label="Warranty start">
        <Input
          data-testid="asset-warranty-start-input"
          onChange={(event) => update("warranty_start", event.target.value)}
          type="date"
          value={form.warranty_start}
        />
      </Field>
      <Field label="Warranty end">
        <Input
          data-testid="asset-warranty-end-input"
          onChange={(event) => update("warranty_end", event.target.value)}
          type="date"
          value={form.warranty_end}
        />
      </Field>
      <div className="sm:col-span-2">
        <Field label="Notes">
          <Textarea
            data-testid="asset-notes-input"
            onChange={(event) => update("notes", event.target.value)}
            value={form.notes}
          />
        </Field>
      </div>
    </div>
  );
}

function AssetDetail({ assetId, onChanged, onClose }) {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const [moveLocation, setMoveLocation] = useState("");
  const [lifecycle, setLifecycle] = useState("RETIRED");
  const [reason, setReason] = useState("");
  const fileInput = useRef(null);

  const load = async () => {
    if (!assetId) return;
    try {
      const response = await api.get(`/society-assets/assets/${assetId}`);
      setData(response.data);
      setEdit(response.data.asset);
      setMoveLocation(response.data.asset.location || "");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Asset is unavailable");
      onClose();
    }
  };

  useEffect(() => {
    setData(null);
    load();
  }, [assetId]);

  const refresh = () => {
    load();
    onChanged();
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/society-assets/assets/${assetId}`, edit);
      toast.success("Asset details updated");
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update asset");
    }
  };

  const move = async () => {
    try {
      await api.post(`/society-assets/assets/${assetId}/move`, {
        location: moveLocation,
        reason,
      });
      setReason("");
      toast.success("Asset location recorded");
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not move asset");
    }
  };

  const updateLifecycle = async () => {
    try {
      await api.post(`/society-assets/assets/${assetId}/lifecycle`, {
        status: lifecycle,
        reason,
      });
      setReason("");
      toast.success(`Asset marked ${lifecycle.toLowerCase()}`);
      refresh();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not update asset lifecycle",
      );
    }
  };

  const uploadDocument = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post(`/society-assets/assets/${assetId}/documents`, formData);
      toast.success("Asset document uploaded");
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not upload document");
    } finally {
      event.target.value = "";
    }
  };

  const archiveDocument = async (documentId) => {
    try {
      await api.delete(`/society-assets/asset-documents/${documentId}`);
      toast.success("Asset document removed");
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not remove document");
    }
  };

  if (!assetId) return null;
  return (
    <Dialog onOpenChange={(open) => !open && onClose()} open={Boolean(assetId)}>
      <DialogContent
        className="max-h-[92vh] max-w-5xl overflow-y-auto"
        data-testid="asset-360-dialog"
      >
        {!data || !edit ? (
          <Loading />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">
                {data.asset.asset_code} · {data.asset.name}
              </DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="border p-3" data-testid="asset-warranty-state">
                <span className="text-xs text-muted-foreground">Warranty</span>
                <div className="font-semibold">{data.asset.warranty_state}</div>
                <div className="text-xs">
                  {data.asset.warranty_end || "No end date"}
                </div>
              </div>
              <div className="border p-3" data-testid="asset-status-state">
                <span className="text-xs text-muted-foreground">Lifecycle</span>
                <div className="font-semibold">{data.asset.status}</div>
                <div className="text-xs">
                  {data.asset.condition} · {data.asset.criticality}
                </div>
              </div>
              <div className="border p-3" data-testid="asset-location-state">
                <span className="text-xs text-muted-foreground">
                  Current location
                </span>
                <div className="font-semibold">
                  {data.asset.location || "Not recorded"}
                </div>
              </div>
            </div>
            <details className="border p-4" data-testid="asset-edit-panel">
              <summary className="cursor-pointer font-display font-bold">
                Edit asset details
              </summary>
              <div className="mt-4">
                <AssetForm
                  categories={[
                    {
                      category_id: data.asset.category_id,
                      name: data.asset.category_name,
                    },
                  ]}
                  form={edit}
                  onChange={setEdit}
                />
                <Button
                  className="mt-4"
                  data-testid="asset-update-button"
                  onClick={saveEdit}
                >
                  Save Changes
                </Button>
              </div>
            </details>
            <section className="grid gap-4 md:grid-cols-2">
              <div className="border p-4" data-testid="asset-move-panel">
                <div className="mb-3 flex items-center gap-2 font-display font-bold">
                  <MapPin className="h-4 w-4" />
                  Move asset
                </div>
                <Input
                  data-testid="asset-move-location-input"
                  onChange={(event) => setMoveLocation(event.target.value)}
                  placeholder="New location"
                  value={moveLocation}
                />
                <Input
                  className="mt-2"
                  data-testid="asset-move-reason-input"
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Move reason (optional)"
                  value={reason}
                />
                <Button
                  className="mt-3"
                  data-testid="asset-move-button"
                  onClick={move}
                  variant="outline"
                >
                  Record Move
                </Button>
              </div>
              <div className="border p-4" data-testid="asset-lifecycle-panel">
                <div className="mb-3 flex items-center gap-2 font-display font-bold">
                  <Archive className="h-4 w-4" />
                  Retire or dispose
                </div>
                <Select onValueChange={setLifecycle} value={lifecycle}>
                  <SelectTrigger data-testid="asset-lifecycle-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RETIRED">Retire</SelectItem>
                    <SelectItem value="DISPOSED">Dispose</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  className="mt-2"
                  data-testid="asset-lifecycle-reason-input"
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Reason required"
                  value={reason}
                />
                <Button
                  className="mt-3"
                  data-testid="asset-lifecycle-button"
                  onClick={updateLifecycle}
                  variant="outline"
                >
                  Confirm Lifecycle
                </Button>
              </div>
            </section>
            <section className="border p-4" data-testid="asset-documents-panel">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 font-display font-bold">
                  <FileText className="h-4 w-4" />
                  Documents & Photos
                </div>
                <input
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  data-testid={`asset-document-input-${assetId}`}
                  hidden
                  onChange={uploadDocument}
                  ref={fileInput}
                  type="file"
                />
                <Button
                  data-testid={`asset-document-upload-${assetId}`}
                  onClick={() => fileInput.current?.click()}
                  size="sm"
                  variant="outline"
                >
                  <Upload className="mr-1 h-4 w-4" />
                  Upload
                </Button>
              </div>
              {data.documents.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No asset documents yet.
                </p>
              ) : (
                <div className="space-y-2">
                  {data.documents.map((document) => (
                    <div
                      className="flex items-center justify-between gap-3"
                      key={document.asset_document_id}
                    >
                      <a
                        className="text-sm text-primary underline"
                        data-testid={`asset-document-${document.asset_document_id}`}
                        href={
                          `${API}/society-assets/asset-documents/` +
                          `${document.asset_document_id}/download`
                        }
                        rel="noreferrer"
                        target="_blank"
                      >
                        {document.filename}
                      </a>
                      <Button
                        data-testid={`asset-document-archive-${document.asset_document_id}`}
                        onClick={() =>
                          archiveDocument(document.asset_document_id)
                        }
                        size="sm"
                        type="button"
                        variant="ghost"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </section>
            <section className="grid gap-4 lg:grid-cols-2">
              <div className="border p-4" data-testid="asset-history-panel">
                <div className="mb-3 flex items-center gap-2 font-display font-bold">
                  <History className="h-4 w-4" />
                  Lifecycle & location history
                </div>
                <div className="space-y-2">
                  {(data.asset.history || [])
                    .slice()
                    .reverse()
                    .map((event) => (
                      <div
                        className="border-l-2 border-primary/30 pl-3 text-sm"
                        key={event.event_id || `${event.type}-${event.at}`}
                      >
                        <b>{event.type}</b>
                        <span className="text-muted-foreground">
                          {" "}
                          · {event.at}
                        </span>
                        {event.detail && <div>{event.detail}</div>}
                      </div>
                    ))}
                </div>
              </div>
              <div className="border p-4" data-testid="asset-helpdesk-panel">
                <div className="mb-3 flex items-center gap-2 font-display font-bold">
                  <ShieldAlert className="h-4 w-4" />
                  Linked Helpdesk
                </div>
                {data.helpdesk_tickets.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No tickets are linked to this asset.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {data.helpdesk_tickets.map((ticket) => (
                      <Link
                        className="block border p-2 text-sm hover:bg-muted"
                        data-testid={`asset-ticket-${ticket.ticket_id}`}
                        key={ticket.ticket_id}
                        to={`/app/complaints?ticket=${ticket.ticket_id}`}
                      >
                        {ticket.ticket_number} · {ticket.subject}
                        <div className="text-xs text-muted-foreground">
                          {ticket.status}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </section>
            <section className="border p-4" data-testid="asset-amc-panel">
              <div className="mb-3 flex items-center gap-2 font-display font-bold">
                <Wrench className="h-4 w-4" />
                AMC & Service History
              </div>
              {data.amc_contracts?.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No AMC or service contract is linked to this asset.
                </p>
              ) : (
                <div className="grid gap-3 md:grid-cols-2">
                  {data.amc_contracts?.map((contract) => (
                    <Link
                      className="block border p-3 text-sm hover:bg-muted"
                      data-testid={`asset-amc-contract-${contract.contract_id}`}
                      key={contract.contract_id}
                      to="/app/amc"
                    >
                      <div className="font-medium">
                        {contract.contract_number} · {contract.title}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {contract.provider_name} · expires {contract.end_date}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
              {data.recent_service_visits?.length > 0 && (
                <div className="mt-4 border-t pt-3 text-sm">
                  <div className="font-medium">Recent service visits</div>
                  {data.recent_service_visits.slice(0, 3).map((visit) => (
                    <div
                      className="mt-2 border-l-2 border-secondary/40 pl-3"
                      data-testid={`asset-amc-visit-${visit.visit_id}`}
                      key={visit.visit_id}
                    >
                      <b>{visit.visit_date}</b> ·{" "}
                      {visit.work_performed || "Service completed"}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function InventoryWorkspace() {
  const [data, setData] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [storeName, setStoreName] = useState("");
  const [item, setItem] = useState(inventoryDefaults);
  const [transaction, setTransaction] = useState({
    movement_type: "OPENING",
    item_id: "",
    store_id: "",
    quantity: "",
    reference: "",
    reason: "",
  });

  const load = async () => {
    try {
      const [boot, items, stores, current, movements] = await Promise.all([
        api.get("/society-assets/bootstrap"),
        api.get("/society-assets/items"),
        api.get("/society-assets/stores"),
        api.get("/society-assets/stock/current"),
        api.get("/society-assets/stock/movements"),
      ]);
      setData({
        categories: boot.data.item_categories,
        items: items.data,
        stores: stores.data,
        current: current.data,
        movements: movements.data,
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load inventory");
      setData({
        categories: [],
        items: [],
        stores: [],
        current: [],
        movements: [],
      });
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (path, body, success) => {
    try {
      await api.post(path, body);
      toast.success(success);
      load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not save inventory data",
      );
    }
  };
  const createItem = async () => {
    await create("/society-assets/items", item, "Inventory item created");
    setItem(inventoryDefaults);
  };
  const postMovement = async () => {
    if (
      !transaction.item_id ||
      !transaction.store_id ||
      !transaction.quantity
    ) {
      toast.error("Item, store, and quantity are required");
      return;
    }
    await create(
      "/society-assets/stock",
      {
        ...transaction,
        quantity: Number(transaction.quantity),
        idempotency_key: crypto.randomUUID(),
      },
      "Stock movement posted",
    );
    setTransaction({
      movement_type: "OPENING",
      item_id: "",
      store_id: "",
      quantity: "",
      reference: "",
      reason: "",
    });
  };

  if (!data) return <Loading />;
  const lowStock = data.current.filter((row) => row.low_stock);
  return (
    <div>
      <div
        className="mb-6 border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
        data-testid="store-transfer-deferred-notice"
      >
        <div className="flex items-center gap-2 font-semibold">
          <ArrowRightLeft className="h-4 w-4" />
          Store Transfer is deferred for pilot release
        </div>
        <p className="mt-1">
          It will be enabled only after transaction-capable MongoDB
          infrastructure and the required integrity verification are complete.
        </p>
      </div>
      <section className="grid gap-4 md:grid-cols-3">
        <StatCard
          icon={Boxes}
          label="Stock positions"
          testid="inventory-stock-kpi"
          value={String(data.current.length)}
        />
        <StatCard
          icon={ShieldAlert}
          label="Low stock"
          testid="inventory-low-stock-kpi"
          tone="amber"
          value={String(lowStock.length)}
        />
        <StatCard
          icon={PackagePlus}
          label="Items"
          testid="inventory-items-kpi"
          tone="secondary"
          value={String(data.items.length)}
        />
      </section>
      <section className="mt-6 grid gap-4 xl:grid-cols-3">
        <div className="border bg-card p-4">
          <div className="mb-3 font-display font-bold">
            Inventory Categories
          </div>
          <div className="flex gap-2">
            <Input
              data-testid="inventory-category-input"
              onChange={(event) => setCategoryName(event.target.value)}
              placeholder="Electrical spares"
              value={categoryName}
            />
            <Button
              data-testid="inventory-category-add"
              onClick={() => {
                create(
                  "/society-assets/inventory/categories",
                  { name: categoryName },
                  "Inventory category added",
                );
                setCategoryName("");
              }}
              size="icon"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {data.categories.map((category) => (
              <span
                className="border px-2 py-1 text-xs"
                data-testid={`inventory-category-${category.category_id}`}
                key={category.category_id}
              >
                {category.name}
              </span>
            ))}
          </div>
        </div>
        <div className="border bg-card p-4">
          <div className="mb-3 font-display font-bold">Stores</div>
          <div className="flex gap-2">
            <Input
              data-testid="inventory-store-input"
              onChange={(event) => setStoreName(event.target.value)}
              placeholder="Main Store"
              value={storeName}
            />
            <Button
              data-testid="inventory-store-add"
              onClick={() => {
                create(
                  "/society-assets/stores",
                  { name: storeName },
                  "Store added",
                );
                setStoreName("");
              }}
              size="icon"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {data.stores.map((store) => (
              <span
                className="border px-2 py-1 text-xs"
                data-testid={`inventory-store-${store.store_id}`}
                key={store.store_id}
              >
                {store.name}
              </span>
            ))}
          </div>
        </div>
        <div className="border bg-card p-4">
          <div className="mb-3 font-display font-bold">Inventory Reports</div>
          <p className="mb-3 text-sm text-muted-foreground">
            Export live current stock or review low-stock positions.
          </p>
          <div className="flex flex-wrap gap-2">
            <a
              data-testid="inventory-current-csv"
              href={`${API}/society-assets/reports/inventory?report=current&format=csv`}
            >
              <Button size="sm" variant="outline">
                Current Stock CSV
              </Button>
            </a>
            <a
              data-testid="inventory-low-stock-csv"
              href={`${API}/society-assets/reports/inventory?report=low-stock&format=csv`}
            >
              <Button size="sm" variant="outline">
                Low Stock CSV
              </Button>
            </a>
          </div>
        </div>
      </section>
      <section className="mt-6 grid gap-5 xl:grid-cols-2">
        <div className="border bg-card p-4" data-testid="inventory-item-master">
          <div className="mb-4 font-display font-bold">Item Master</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Item name">
              <Input
                data-testid="inventory-item-name"
                onChange={(event) =>
                  setItem({ ...item, name: event.target.value })
                }
                value={item.name}
              />
            </Field>
            <Field label="Category">
              <Select
                onValueChange={(value) =>
                  setItem({ ...item, category_id: value })
                }
                value={item.category_id}
              >
                <SelectTrigger data-testid="inventory-item-category">
                  <SelectValue placeholder="Optional category" />
                </SelectTrigger>
                <SelectContent>
                  {data.categories
                    .filter((category) => category.active)
                    .map((category) => (
                      <SelectItem
                        key={category.category_id}
                        value={category.category_id}
                      >
                        {category.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Unit">
              <Input
                data-testid="inventory-item-uom"
                onChange={(event) =>
                  setItem({ ...item, uom: event.target.value })
                }
                value={item.uom}
              />
            </Field>
            <Field label="Minimum stock">
              <Input
                data-testid="inventory-item-minimum"
                min="0"
                onChange={(event) =>
                  setItem({ ...item, minimum_stock: event.target.value })
                }
                type="number"
                value={item.minimum_stock}
              />
            </Field>
          </div>
          <Button
            className="mt-4"
            data-testid="inventory-item-save"
            onClick={createItem}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add Item
          </Button>
        </div>
        <div
          className="border bg-card p-4"
          data-testid="inventory-transaction-form"
        >
          <div className="mb-4 font-display font-bold">
            Post Safe Stock Movement
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Movement">
              <Select
                onValueChange={(value) =>
                  setTransaction({ ...transaction, movement_type: value })
                }
                value={transaction.movement_type}
              >
                <SelectTrigger data-testid="inventory-movement-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[
                    "OPENING",
                    "STOCK_IN",
                    "ISSUE",
                    "RETURN",
                    "ADJUSTMENT_IN",
                    "ADJUSTMENT_OUT",
                  ].map((type) => (
                    <SelectItem key={type} value={type}>
                      {type.replace("_", " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Item">
              <Select
                onValueChange={(value) =>
                  setTransaction({ ...transaction, item_id: value })
                }
                value={transaction.item_id}
              >
                <SelectTrigger data-testid="inventory-movement-item">
                  <SelectValue placeholder="Select item" />
                </SelectTrigger>
                <SelectContent>
                  {data.items
                    .filter((row) => row.active)
                    .map((row) => (
                      <SelectItem key={row.item_id} value={row.item_id}>
                        {row.item_code} · {row.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Store">
              <Select
                onValueChange={(value) =>
                  setTransaction({ ...transaction, store_id: value })
                }
                value={transaction.store_id}
              >
                <SelectTrigger data-testid="inventory-movement-store">
                  <SelectValue placeholder="Select store" />
                </SelectTrigger>
                <SelectContent>
                  {data.stores
                    .filter((row) => row.active)
                    .map((row) => (
                      <SelectItem key={row.store_id} value={row.store_id}>
                        {row.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Quantity">
              <Input
                data-testid="inventory-movement-quantity"
                min="0.0001"
                onChange={(event) =>
                  setTransaction({
                    ...transaction,
                    quantity: event.target.value,
                  })
                }
                type="number"
                value={transaction.quantity}
              />
            </Field>
            <Field label="Reference">
              <Input
                data-testid="inventory-movement-reference"
                onChange={(event) =>
                  setTransaction({
                    ...transaction,
                    reference: event.target.value,
                  })
                }
                value={transaction.reference}
              />
            </Field>
            <Field label="Reason">
              <Input
                data-testid="inventory-movement-reason"
                onChange={(event) =>
                  setTransaction({ ...transaction, reason: event.target.value })
                }
                value={transaction.reason}
              />
            </Field>
          </div>
          <Button
            className="mt-4"
            data-testid="inventory-movement-submit"
            onClick={postMovement}
          >
            Post Movement
          </Button>
        </div>
      </section>
      <section className="mt-6">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-lg font-bold">
            Current Stock & Low Stock
          </h2>
          <Button
            data-testid="inventory-refresh"
            onClick={load}
            size="sm"
            variant="outline"
          >
            <RefreshCw className="mr-1 h-4 w-4" />
            Refresh
          </Button>
        </div>
        {data.current.length === 0 ? (
          <EmptyMessage testid="inventory-current-empty">
            No posted inventory stock yet.
          </EmptyMessage>
        ) : (
          <div
            className="overflow-x-auto border bg-card"
            data-testid="inventory-current-table"
          >
            <table className="min-w-[760px] w-full text-sm">
              <thead className="bg-muted/60 text-left">
                <tr>
                  <th className="p-3">Item</th>
                  <th className="p-3">Store</th>
                  <th className="p-3">On hand</th>
                  <th className="p-3">Minimum</th>
                  <th className="p-3">State</th>
                </tr>
              </thead>
              <tbody>
                {data.current.map((row) => (
                  <tr
                    className="border-t"
                    data-testid={`inventory-stock-${row.item_id}-${row.store_id}`}
                    key={`${row.item_id}-${row.store_id}`}
                  >
                    <td className="p-3">
                      <b>{row.item_name}</b>
                      <div className="font-mono text-xs text-muted-foreground">
                        {row.item_code}
                      </div>
                    </td>
                    <td className="p-3">{row.store_name}</td>
                    <td className="p-3">
                      {row.quantity} {row.uom}
                    </td>
                    <td className="p-3">{row.minimum_stock}</td>
                    <td className="p-3">
                      <span
                        className={
                          row.low_stock ? "text-amber-700" : "text-secondary"
                        }
                      >
                        {row.low_stock ? "LOW STOCK" : "Healthy"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section className="mt-6">
        <h2 className="mb-3 font-display text-lg font-bold">
          Stock Register & Item History
        </h2>
        {data.movements.length === 0 ? (
          <EmptyMessage testid="inventory-history-empty">
            Posted movements will appear here.
          </EmptyMessage>
        ) : (
          <div
            className="overflow-x-auto border bg-card"
            data-testid="inventory-history-table"
          >
            <table className="min-w-[900px] w-full text-sm">
              <thead className="bg-muted/60 text-left">
                <tr>
                  <th className="p-3">When</th>
                  <th className="p-3">Item</th>
                  <th className="p-3">Store</th>
                  <th className="p-3">Movement</th>
                  <th className="p-3">Quantity</th>
                  <th className="p-3">Reference</th>
                </tr>
              </thead>
              <tbody>
                {data.movements.slice(0, 100).map((row) => (
                  <tr
                    className="border-t"
                    data-testid={`inventory-movement-${row.movement_id}`}
                    key={row.movement_id}
                  >
                    <td className="p-3 text-xs">
                      {row.created_at?.slice(0, 16).replace("T", " ")}
                    </td>
                    <td className="p-3">{row.item_name}</td>
                    <td className="p-3">{row.store_name}</td>
                    <td className="p-3">{row.movement_type}</td>
                    <td className="p-3">
                      {row.quantity} {row.uom}
                    </td>
                    <td className="p-3">{row.reference || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
